<script setup lang="ts">
import Chart from '@astrodraw/astrochart';
import { computed } from 'vue';
import NoSwishData from './NoSwishData.vue';

const store = useStore();
const swishData = computed(() => store.currentSwishData);

const date = ref(new Date());
const clockHandler = setInterval(() => date.value = new Date(), 500);
const formattedTimestamp = computed(() => {
  if (!swishData.value) return '';
  return date.value.toLocaleString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true
  });
});

const fetchHandler = setInterval(store.fetchCurrentSwishData, 10000)


const radixChartData = computed(() => ({
  planets: swishData.value?.positions.map(x => {
    const zodiacStartDegrees: { [key: string]: number } = {
      Aries: 0, Taurus: 30, Gemini: 60, Cancer: 90, Leo: 120, Virgo: 150,
      Libra: 180, Scorpio: 210, Sagittarius: 240, Capricorn: 270, Aquarius: 300, Pisces: 330
    };
    const convertPosition = (sign: string, degrees: number, minutes: number) => {
      const decimalDegrees = degrees + (minutes / 60);
      // eslint-disable-next-line security/detect-object-injection
      return Math.round(zodiacStartDegrees[sign] + decimalDegrees);
    };
    const position = convertPosition(x.sign, x.degrees, x.minutes);
    return { planet: x.planet.replace("The ", ""), position: position }
  }).reduce((acc: {[key:string]: number[]}, { planet, position }) => {
    // eslint-disable-next-line security/detect-object-injection
    acc[planet] = [position];
    return acc;
  }, {}) || {},
  cusps: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  // Note: Cusps data is not provided in the original geo positions, so we're using the example values
  // cusps: [296, 350, 30, 56, 75, 94, 116, 170, 210, 236, 255, 274]
}));


/**
 * Vue shim for radix library, have to construct library after the element has been rendered so it can find div
 */
const radix = ref();
console.log(radix)
let chart: Chart | undefined;
watch([radix, radixChartData], ([newRadix, newData]) => {
  if (newRadix?.id) {
    if (!chart) chart = new Chart(newRadix.id, 400, 400);
    chart.radix(newData as any)
  }
})

onBeforeUnmount(() => {
  chart = undefined;
  clearInterval(clockHandler);
  clearInterval(fetchHandler);
})
</script>

<template>
  <NoSwishData v-if="!swishData" />
  <div v-else
    class="bg-gray-800 bg-opacity-30 text-white p-4 sm:p-6 rounded-lg shadow-lg backdrop-blur-[2px]">
    <h2 class="text-2xl font-bold text-center mb-6">{{ formattedTimestamp }}</h2>

    <!-- Main Flex Container -->
    <div class="flex flex-col space-y-6 lg:flex-row lg:space-x-6 lg:space-y-0">

      <!-- Left Column (Other Sections) -->
      <div class="w-full lg:w-1/2 space-y-4 order-1 lg:order-1">

        <!-- Now Section -->
        <div>
          <h3 class="text-xl font-semibold mb-2">At This Time Right Now.</h3>
          <ul class="list-disc list-inside">
            <li v-for="(item, index) in swishData?.now" :key="index">{{ item }}</li>
          </ul>
        </div>

        <!-- Positions Section -->
        <div>
          <h3 class="text-xl font-semibold mb-2">Up to the minute Geo Positions.</h3>
          <div class="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <div class="flex flex-col text-sm items-center justify-center">
              <table class="table-auto w-full">
                <tbody>
                  <tr v-for="(item, index) in swishData?.positions" :key="index">
                    <td class="pl-4 pr-1 py-1">{{ item.planet }}</td>
                    <td class="pr-4 pl-1 py-1">{{ item.degrees }}&deg;</td>
                    <td class="pl-4 pr-1 py-1">{{ item.sign }}</td>
                    <td class="pr-4 pl-1 py-1">{{ item.minutes }}&#8242;</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div id="radix" ref="radix" class="h-100 w-100"></div>
          </div>
        </div>

        <!-- Space Oracle Section -->
        <div>
          <h3 class="text-xl font-semibold mb-2">Local Space Oracle</h3>
          <div class="space-y-2 text-sm">
            <div v-for="(item, index) in swishData?.spaceOracle" :key="index" class="">
              {{ item }}
            </div>
          </div>
        </div>

        <!-- Global Situation Section -->
        <div>
          <h3 class="text-xl font-semibold mb-2">Global Situation</h3>
          <div v-for="(item, index) in swishData?.globalSituation" :key="index" class="">
            {{ item }}
          </div>
        </div>
      </div>

      <!-- Right Column (Top Ten) -->
      <div class="w-full lg:w-1/2 order-2 lg:order-last">
        <h3 class="text-xl font-semibold mb-2">Hit Parade. Top 10.</h3>
        <ul class="space-y-2">
          <li v-for="item in swishData?.topTen" :key="item.position"
            class="bg-gray-800 bg-opacity-30 backdrop-blur-[3px] px-3 py-2 rounded">
            <span class="font-bold">{{ item.position }}:</span>
            <span class="ml-2">{{ item.value }}</span>
            <p class="text-sm mt-1">{{ item.description }}</p>
          </li>
        </ul>
      </div>
    </div>

    <div class="text-sm text-gray-400 mt-6">
      Timestamp: {{ new Date(swishData?.timestamp * 1000).toLocaleString() }}
    </div>
  </div>


</template>
